// File: src/pages/PaymentsPage.jsx

import React, { useEffect, useState } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Modal, Button, Form } from 'react-bootstrap';

export default function PaymentsPage() {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [manualAdvance, setManualAdvance] = useState(false);
  const [sessionAllocated, setSessionAllocated] = useState(0);

  const navigate = useNavigate();

  // Add Advance modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [advAmount, setAdvAmount] = useState('');
  const [advRemarks, setAdvRemarks] = useState('');
  const [paymentDate, setPaymentDate] = useState(dayjs().format('YYYY-MM-DD'));

  // Handle Add Advance
  const handleAddAdvance = async () => {
    if (!selected || !advAmount) {
      alert('Enter advance amount');
      return;
    }
    const partyType = selected.type;
    const idKey = partyType === 'customer' ? 'CustomerID' : 'SupplierID';
    const entityId = selected[idKey];
    // Use allocate API to record advance
    await api.post('/payments/customer/allocate', {
      customerId: partyType === 'customer' ? entityId : undefined,
      totalAmount: Number(advAmount),
      mode: 'manual',
      remarks: advRemarks,
      allocations: [{ invoiceId: 0, amount: 0 }]
    });
    // close modal and clear
    setShowAddModal(false);
    setAdvAmount('');
    setAdvRemarks('');
    // refresh entire page to reflect new data
    window.location.reload();
  };

  useEffect(() => {
    if (!search) return setResults([]);
    const timeout = setTimeout(async () => {
      const [customers, suppliers] = await Promise.all([
        api.get('/customers'),
        api.get('/purchase/suppliers')
      ]);
      const lower = search.toLowerCase();
      const matched = [
        ...customers.data
          .filter(c => c.Name.toLowerCase().includes(lower))
          .map(c => ({ type: 'customer', data: c })),
        ...suppliers.data
          .filter(s => s.Name.toLowerCase().includes(lower))
          .map(s => ({ type: 'supplier', data: s }))
      ];
      setResults(matched);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const fetchInvoices = async (party) => {
    if (party.type === 'customer') {
      const res = await api.get('/invoices', { params: { userId: party.data.UserID || 3 } });
      const data = res.data.filter(inv => String(inv.CustomerID) === String(party.data.CustomerID));
      setInvoices(data);
    } else {
      const res = await api.get('/purchase/purchase-bills', { params: { userId: party.data.UserID || 3 } });
      const data = res.data.filter(b => String(b.SupplierID) === String(party.data.SupplierID));
      setInvoices(data);
    }
  };

  const handleSelect = async (party) => {
    let fullData = party.data;
    if (party.type === 'supplier') {
      const res = await api.get(`/purchase/suppliers/${party.data.SupplierID}`);
      fullData = res.data;
    }
    setSelected({ ...fullData, type: party.type });
    setSearch(fullData.Name);
    setResults([]);
    fetchInvoices({ type: party.type, data: fullData });
  };

  const pendingInvoices = invoices.filter(inv => {
    const total = Number(inv.TotalAmount || inv.Total || 0) - Number(inv.Discount || 0);
    const paid = Number(inv.PaidAmount || 0);
    return total - paid > 0.009;
  });
  const pendingInvoiceNos = pendingInvoices.map(inv => inv.InvoiceNo || inv.BillNo);
  const pendingTotal = pendingInvoices.reduce((sum, inv) => {
    const total = Number(inv.TotalAmount || inv.Total || 0) - Number(inv.Discount || 0);
    const paid = Number(inv.PaidAmount || 0);
    return sum + Math.max(total - paid, 0);
  }, 0);

  const totalAllocated = Object.values(allocations).reduce((sum, v) => sum + parseFloat(v || 0), 0);
  const advanceAmount = Number(selected?.AdvanceAmount || 0);

  const handleAutoApply = async () => {
    let remaining = advanceAmount;
    for (const inv of invoices) {
      const id = inv.InvoiceID || inv.PurchaseBillID;
      const total = Number(inv.TotalAmount || inv.Total || 0) - Number(inv.Discount || 0);
      const paid = Number(inv.PaidAmount || 0);
      const pending = Math.max(total - paid, 0);
      if (remaining <= 0) break;
      const applyAmt = Math.min(remaining, pending);
      if (applyAmt > 0) {
        const url = inv.InvoiceID
          ? `/invoices/${id}/pay`
          : `/purchase/purchase-bills/${id}/payments`;
        await api.post(url, {
          PaidAmount: applyAmt,
          Amount: applyAmt,
          PaymentMode: 'Advance',
          Remarks: 'Auto Advance',
          PaymentDate: dayjs().format('YYYY-MM-DD')
        });
        remaining -= applyAmt;
      }
    }
    const deductUrl = selected.CustomerID
      ? `/customers/${selected.CustomerID}/advance-deduct`
      : `/purchase/suppliers/${selected.SupplierID}/advance-deduct`;
    await api.post(deductUrl, { amount: advanceAmount - remaining });
    alert('Advance auto-applied successfully');
    fetchInvoices({ type: selected.type, data: selected });
  };

  const handleManualApply = async () => {
    for (let [id, amt] of Object.entries(allocations)) {
      const inv = invoices.find(x => (x.InvoiceID || x.PurchaseBillID) == id);
      if (!inv) continue;
      const amount = parseFloat(amt);
      if (amount > 0) {
        const url = inv.InvoiceID
          ? `/invoices/${id}/pay`
          : `/purchase/purchase-bills/${id}/payments`;
        await api.post(url, {
          PaidAmount: amount,
          Amount: amount,
          PaymentMode: 'Advance',
          Remarks: 'Manual Advance Allocation',
          PaymentDate: dayjs().format('YYYY-MM-DD')
        });
      }
    }
    const deductUrl = selected.CustomerID
      ? `/customers/${selected.CustomerID}/advance-deduct`
      : `/purchase/suppliers/${selected.SupplierID}/advance-deduct`;
    await api.post(deductUrl, { amount: totalAllocated });
    alert('Manual advance applied successfully');
    setManualAdvance(false);
    fetchInvoices({ type: selected.type, data: selected });
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold text-primary">Payments Manager</h4>
        <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>← Back</button>
      </div>

      {/* Add Advance Button */}
      {selected && (
        <button className="btn btn-outline-primary mb-3" onClick={() => setShowAddModal(true)}>
          Add Advance
        </button>
      )}

      <div className="mb-3">
        <label className="form-label">Search Customer or Supplier</label>
        <input
          className="form-control"
          value={search}
          onChange={e => { setSearch(e.target.value); setSelected(null); }}
        />
        {search && results.length > 0 && !selected && (
          <ul className="list-group mt-2">
            {results.map((r, i) => (
              <li
                key={i}
                className="list-group-item list-group-item-action"
                onClick={() => handleSelect(r)}
              >
                {r.data.Name} ({r.type})
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <>
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card text-center border-success">
                <div className="card-body">
                  <div className="text-muted">Advance Available</div>
                  <h5 className="text-success">₹{advanceAmount.toFixed(2)}</h5>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-center border-danger">
                <div className="card-body">
                  <div className="text-muted">Pending Invoice Amount</div>
                  <h5 className="text-danger mb-2">₹{pendingTotal.toFixed(2)}</h5>
                  <small className="text-muted">Invoices: {pendingInvoiceNos.join(', ')}</small>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-center border-info">
                <div className="card-body">
                  <div className="text-muted">Allocated This Session</div>
                  <h5 className="text-info">₹{totalAllocated.toFixed(2)}</h5>
                </div>
              </div>
            </div>
          </div>

          {advanceAmount > 0 && (
            <div className="alert alert-info d-flex justify-content-between align-items-center">
              <div>
                <strong>Advance Available:</strong> ₹{advanceAmount.toFixed(2)}
              </div>
              <div>
                <button className="btn btn-sm btn-primary me-2" onClick={handleAutoApply}>Auto Apply</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setManualAdvance(true)}>Manual Apply</button>
              </div>
            </div>
          )}

          {manualAdvance && invoices.length > 0 && (
            <div className="table-responsive mb-3">
              <table className="table table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{selected.CustomerID ? 'Invoice No' : 'Bill No'}</th>
                    <th>Date</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Pending</th>
                    <th>Allocate</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvoices.map(inv => {
                    const id = inv.InvoiceID || inv.PurchaseBillID;
                    const total = Number(inv.TotalAmount || inv.Total || 0) - Number(inv.Discount || 0);
                    const paid = Number(inv.PaidAmount || 0);
                    const pending = Math.max(total - paid, 0);
                    return (
                      <tr key={id}>
                        <td>{inv.InvoiceNo || inv.BillNo}</td>
                        <td>{dayjs(inv.InvoiceDate || inv.BillDate).format('DD/MM/YYYY')}</td>
                        <td>₹{total.toFixed(2)}</td>
                        <td>₹{paid.toFixed(2)}</td>
                        <td>₹{pending.toFixed(2)}</td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={allocations[id] || ''}
                            onChange={e => setAllocations({ ...allocations, [id]: e.target.value })}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button className="btn btn-primary" onClick={handleManualApply}>Apply Manual Advance</button>
            </div>
          )}
        </>
      )}

      {/* Add Advance Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Advance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                value={advAmount}
                onChange={e => setAdvAmount(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                value={advRemarks}
                onChange={e => setAdvRemarks(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAddAdvance}>Add</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

